import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LINE_PAYMENT_METHODS, RESTORE_PAYMENT_CODES, SKIP_PAYMENT_CODE } from '@app/shared';
import type { CardPaymentInput, ChargeQuoteDto, CustomerDto, LinePaymentMethod } from '@app/shared';
import { useNotify } from '../../components/NotifyProvider';
import { api, errorMessage } from '../../lib/api';
import { formatCents } from '../../lib/format';
import { useAuth } from '../auth/AuthProvider';
import { CardField, useCardsAvailable } from '../billing/CardField';
import type { CardFieldHandle } from '../billing/CardField';
import { customerPath } from './parts';

type Method = LinePaymentMethod | 'skip';

/**
 * "Payment Information" of Update Account Status (PC95) when a line suspended because its plan ran
 * out is restored: the reference's ways of paying with their codes (Cash PC415, Money Order PC416,
 * Money Gram PC417, Wallet PC418, Skip Payment PC99), the amounts (Plan Amount, tax, processing fee,
 * late and restore fees, Total Amount Due) and "Update Account Status", which pays and restores.
 */
export function RestorePayment({ customer }: { customer: CustomerDto }) {
  const { can } = useAuth();
  const notify = useNotify();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const cardsAvailable = useCardsAvailable();
  const cardRef = useRef<CardFieldHandle>(null);
  const methods: Method[] = [
    ...LINE_PAYMENT_METHODS.map((m) => m.key).filter((k) => {
      const code = RESTORE_PAYMENT_CODES[k];
      return code ? can(code) && (k !== 'card' || cardsAvailable) : false;
    }),
    ...(can(SKIP_PAYMENT_CODE) ? (['skip'] as const) : []),
  ];
  const [method, setMethod] = useState<Method | ''>(methods[0] ?? '');
  const [receipt, setReceipt] = useState('');
  const [failure, setFailure] = useState('');
  const query = method === 'skip' ? { skipPayment: 'true' } : { method };
  const quote = useQuery({
    queryKey: ['customers', customer.id, 'restore-quote', query],
    queryFn: () =>
      api.get<ChargeQuoteDto>(`/customers/${customer.id}/restore-payment/quote`, query),
    enabled: Boolean(method),
  });
  const pay = useMutation({
    mutationFn: (card?: CardPaymentInput) =>
      api.post(
        `/customers/${customer.id}/restore-payment`,
        method === 'skip'
          ? { skipPayment: true, mode: 'Instant' }
          : { method, receiptNumber: receipt, mode: 'Instant', card },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customers'] }),
  });
  const submit = async () => {
    setFailure('');
    try {
      const card = method === 'card' ? await cardRef.current?.collect() : undefined;
      await pay.mutateAsync(card);
      notify.success('Payment received; the line is restored.');
      navigate(customerPath(customer.id));
    } catch (err) {
      setFailure(errorMessage(err));
    }
  };
  if (!methods.length)
    return (
      <Alert severity="info">
        Restoring this line takes a payment; your user group has no way of paying for it.
      </Alert>
    );
  const q = quote.data;
  const charge = (label: string) => q?.charges.find((c) => c.label === label)?.amountCents ?? 0;
  const lines: Array<[string, number]> = q
    ? [
        ['Plan Amount', q.baseCents],
        ['Total Tax', q.taxCents],
        ['Processing Fee', q.processingFeeCents],
        ['Late Fee Charge', charge('Late Fee Charge')],
        ['Restore Fee Charge', charge('Restore Fee Charge')],
        ...(q.discountCents ? ([['Discount', -q.discountCents]] as Array<[string, number]>) : []),
      ]
    : [];
  return (
    <Box aria-label="Payment Information" component="section">
      <Typography variant="h3" sx={{ mb: 1 }}>
        Payment Information
      </Typography>
      {failure ? (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {failure}
        </Alert>
      ) : null}
      <FormControl sx={{ mb: 1.5 }}>
        <FormLabel id="restore-method">Payment Method</FormLabel>
        <RadioGroup
          row
          aria-labelledby="restore-method"
          value={method}
          onChange={(e) => setMethod(e.target.value as Method)}
        >
          {methods.map((m) => (
            <FormControlLabel
              key={m}
              value={m}
              control={<Radio size="small" />}
              label={
                m === 'skip'
                  ? `Skip Payment (${SKIP_PAYMENT_CODE})`
                  : `${LINE_PAYMENT_METHODS.find((x) => x.key === m)!.label} (${RESTORE_PAYMENT_CODES[m]})`
              }
            />
          ))}
        </RadioGroup>
      </FormControl>
      {method === 'cash' || method === 'moneyOrder' || method === 'moneyGram' ? (
        <TextField
          label="Receipt Number"
          value={receipt}
          onChange={(e) => setReceipt(e.target.value)}
          slotProps={{ htmlInput: { maxLength: 30 } }}
          sx={{ display: 'block', mb: 1.5 }}
        />
      ) : null}
      {method === 'card' ? (
        <Box sx={{ mb: 1.5, maxWidth: 480 }}>
          <CardField ref={cardRef} customerId={customer.id} />
        </Box>
      ) : null}
      {method === 'wallet' ? (
        <Typography sx={{ mb: 1 }}>
          Wallet Balance: {formatCents(customer.other.walletBalanceCents)}
        </Typography>
      ) : null}
      {quote.error ? <Alert severity="error">{errorMessage(quote.error)}</Alert> : null}
      {q ? (
        <Box
          component="dl"
          aria-label="Restore amounts"
          sx={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: 0.5, maxWidth: 360, m: 0 }}
        >
          {lines.map(([label, cents]) => (
            <Box key={label} sx={{ display: 'contents' }}>
              <Typography component="dt">{label}</Typography>
              <Typography component="dd" sx={{ m: 0, textAlign: 'right' }}>
                {formatCents(cents)}
              </Typography>
            </Box>
          ))}
          <Typography component="dt" sx={{ fontWeight: 600 }}>
            Total Amount Due
          </Typography>
          <Typography component="dd" sx={{ m: 0, textAlign: 'right', fontWeight: 600 }}>
            {formatCents(q.totalCents)}
          </Typography>
        </Box>
      ) : null}
      <Button
        variant="contained"
        sx={{ mt: 2 }}
        disabled={pay.isPending || !method || !q}
        onClick={() => void submit()}
      >
        Update Account Status
      </Button>
    </Box>
  );
}
